/**
 * use-stripe-checkout.ts — Stripe direct Checkout App 配置 API 封装（ADM-PAY-S007 / A8 §9）
 *
 * 对应后端：`/api/v1/integrations/stripe/checkout-configs*`（全部 admin+ mfa）——
 * 按 App 维护 Stripe Account、credential route、固定回跳 URL 与启停，并做连接验证。
 *
 * **秘密边界**：`restricted_key` 只在 PUT 请求体里出现一次，写入当前环境 credential store 后即弃；
 * 响应**永不回显** key / tail / hash / 长度（A8 §9.1/§9.3）。前端不缓存、不回填该字段。
 *
 * 每方法 = 一次 apiRequest（自动带 JWT + X-Request-Id + 解 envelope 返 data，失败抛 AdminApiError）。
 * 请求/响应类型一律从 @novel-admin/shared 取（禁 web 侧重定义）。
 */

import type {
  StripeCheckoutConfigResponse,
  StripeCheckoutConnectionTestResponse,
  UpsertStripeCheckoutConfigRequest,
} from '@novel-admin/shared';
import { apiRequest, apiRequestWithMessage } from '~/api/client';

const BASE = '/api/v1/integrations/stripe/checkout-configs';

export const stripeCheckoutApi = {
  /** 列 App Stripe Checkout 配置（有界不分页；不含任何秘密字段） */
  listConfigs: () => apiRequest<StripeCheckoutConfigResponse[]>({ method: 'GET', path: BASE }),

  /**
   * 写 Account / credential route / 回跳 URL。创建或更新 **inactive** 配置——不经本接口激活。
   * `Idempotency-Key` 必带（副作用跨 credential store 与 DB 两个存储，重试须可安全重放）。
   */
  upsertConfig: (appId: string, body: UpsertStripeCheckoutConfigRequest, idempotencyKey: string) =>
    apiRequest<StripeCheckoutConfigResponse>({
      method: 'PUT',
      path: `${BASE}/${appId}`,
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  /**
   * 测试连接：验 credential / Checkout Sessions 与恢复权限；IOS/ANDROID 另报原生一次性付款证明读取能力。
   * 证明读取未就绪时响应壳 message 带运营提示（A8 §9.4 要求后台展示），故连同 message 一起返回。
   */
  testConnection: (appId: string) =>
    apiRequestWithMessage<StripeCheckoutConnectionTestResponse>({
      method: 'POST',
      path: `${BASE}/${appId}/test`,
    }),

  /** 激活（实时连接验证通过后置 is_active=true） */
  activateConfig: (appId: string) =>
    apiRequest<StripeCheckoutConfigResponse>({
      method: 'POST',
      path: `${BASE}/${appId}/activate`,
    }),

  /** 停用（App 级 kill switch；不调用 Stripe，已 READY operation 不追溯） */
  deactivateConfig: (appId: string) =>
    apiRequest<StripeCheckoutConfigResponse>({
      method: 'POST',
      path: `${BASE}/${appId}/deactivate`,
    }),
};
